import { useEffect, useMemo, useState } from 'react';
import {
    useAllPracticeFeatureFlags,
    usePracticeFeatureFlags,
    useTogglePracticeFeatureFlag
} from '../../../../../hooks/usePracticeFeatureFlags';
import {
    KNOWN_PRACTICE_FEATURES,
    knownFeatureLabel
} from '../../../../../services/practiceFeatureFlags';
import { useToast } from '../../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../../utils/apiError';
import { AddBetaFeatureForm } from './AddBetaFeatureForm';
import type { FeatureOption } from './AddBetaFeatureForm';
import { BetaFeatureRow } from './BetaFeatureRow';

interface PracticeBetaAccessSectionProps {
    practiceId: string;
}

// The Details tab's "Beta access" rail pane - per-practice beta feature
// toggles, read by the practitioner dashboard (see
// services/practiceFeatureFlags.ts). Wrapped in its own card since the
// Details tab no longer has an outer wrapping box (see DetailsTab) - the
// page itself scrolls now, not this pane.
export function PracticeBetaAccessSection({ practiceId }: PracticeBetaAccessSectionProps) {
    const { showToast } = useToast();
    const { features, isLoading, isError, error } = usePracticeFeatureFlags(practiceId);
    const { data: allFeatureRows } = useAllPracticeFeatureFlags();
    const toggleFeature = useTogglePracticeFeatureFlag(practiceId);
    const [pendingFeature, setPendingFeature] = useState<string | null>(null);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load beta features'), 'error');
        }
    }, [isError, error, showToast]);

    // Catalogued features by friendly name, then any in-use key that predates
    // the catalog. Both exclude what's already enabled on this practice.
    const options = useMemo<FeatureOption[]>(() => {
        const enabled = new Set(features);
        const curatedKeys = new Set(KNOWN_PRACTICE_FEATURES.map((feature) => feature.key));
        const curated = KNOWN_PRACTICE_FEATURES.filter((feature) => !enabled.has(feature.key)).map(
            (feature) => ({ value: feature.key, label: feature.label })
        );
        const inUse = [...new Set((allFeatureRows ?? []).map((row) => row.feature))]
            .filter((feature) => !curatedKeys.has(feature) && !enabled.has(feature))
            .sort()
            .map((feature) => ({ value: feature, label: feature }));
        return [...curated, ...inUse];
    }, [allFeatureRows, features]);

    const handleToggle = async (feature: string, enabled: boolean) => {
        setPendingFeature(feature);
        try {
            await toggleFeature.mutateAsync({ feature, enabled });
            showToast(enabled ? `${feature} enabled` : `${feature} disabled`, 'success');
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to update feature'), 'error');
        } finally {
            setPendingFeature(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5 flex flex-col gap-5 shrink-0">
            <div>
                <h3 className="text-sm font-bold text-gray-800">Beta access</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                    Beta features are per-practice flags read by the practitioner dashboard.
                </p>
            </div>

            <AddBetaFeatureForm
                options={options}
                disabled={pendingFeature !== null}
                onAdd={(feature) => handleToggle(feature, true)}
            />

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            ) : features.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">
                    No beta features enabled for this practice.
                </p>
            ) : (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                    {features.map((feature) => (
                        <BetaFeatureRow
                            key={feature}
                            feature={feature}
                            label={knownFeatureLabel(feature)}
                            disabled={pendingFeature === feature}
                            onRemove={() => void handleToggle(feature, false)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
